
import { Arr } from '@deqo/foundation';
import { hsl } from 'polished';
import { alea } from 'seedrandom';
import PictWords from '../../../@resources/texts/pictwords.json';
import { shuffle } from '../../../CodesForFoundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { MyProfileInfo } from '../../canvas/MyProfileInfo';
import { PointerInfo } from '../../canvas/PointerInfo';
import { Selection } from '../../canvas/Selection';
import { resolveSubjects } from '../../canvas/commands/CommandUtil';
import { clearSelection, deleteSelection } from '../../canvas/commands/SelectionCommands';
import { Phys } from '../../canvas/entities/Phys';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { ContrastColorList } from '../tabularImport/strategies/ContrastColorList';
import { Colors } from './../../../ui';
import { CardPack } from './CardPack';
import { CardPile } from './CardPile';
import { DropZone, DropZoneMode } from './DropZone';
import { dropZoneDrop } from './DropZoneCommands';
import { Hand } from './Hand';
import { HandInfo } from './HandInfo';
import { HoldLayout } from './HoldLayout';
import { ValueCard } from './ValueCard';

const cardDef = (value: number, largeText: string, smallText: string, color: string) => ({ value, largeText, smallText, color });
export type CardDef = ReturnType<typeof cardDef>;

const cardPackDef = (mode: 'sets' | 'pool', open: () => CardDef[]) => ({ mode, open });
export type CardPackDef = ReturnType<typeof cardPackDef>;

const staticSetPackDef = (...items: CardDef[]): CardPackDef => cardPackDef('sets', () => items);
const dynamicPoolPackDef = (open: () => CardDef[]): CardPackDef => cardPackDef('pool', open);

const packs: Record<string, CardPackDef> = {
    planning: staticSetPackDef(
        cardDef(0, '0', '0', hsl((180 / 11) * 10, .65, .45)),
        cardDef(.5, '1/2', '1/2', hsl((180 / 11) * 9, .65, .45)),
        cardDef(1, '1', '1', hsl((180 / 11) * 8, .65, .45)),
        cardDef(2, '2', '2', hsl((180 / 11) * 7, .65, .45)),
        cardDef(3, '3', '3', hsl((180 / 11) * 6, .65, .45)),
        cardDef(5, '5', '5', hsl((180 / 11) * 5, .65, .45)),
        cardDef(8, '8', '8', hsl((180 / 11) * 4, .65, .45)),
        cardDef(13, '13', '13', hsl((180 / 11) * 3, .65, .45)),
        cardDef(20, '20', '20', hsl((180 / 11) * 2, .65, .45)),
        cardDef(40, '40+', '40+', hsl((180 / 11) * 1, .65, .45)),
        cardDef(null, '?', 'IDK', Colors.primary),
    ),
    tshirts: staticSetPackDef(
        cardDef(1, 'XS', 'Very Small', '#03a9f4'),
        cardDef(2, 'S', 'Small', '#009688'),
        cardDef(3, 'M', 'Medium', '#cddc39'),
        cardDef(4, 'L', 'Large', '#ff9800'),
        cardDef(5, 'XL', 'Very Large', '#e91e63'),
        cardDef(99, ':c', 'Too Big', '#795548'),
    ),
    numbers: staticSetPackDef(
        cardDef(1, '1', 'One', Colors.primary),
        cardDef(2, '2', 'Two', Colors.primary),
        cardDef(3, '3', 'Three', Colors.primary),
        cardDef(4, '4', 'Four', Colors.primary),
        cardDef(5, '5', 'Five', Colors.primary),
        cardDef(6, '6', 'Six', Colors.primary),
        cardDef(7, '7', 'Seven', Colors.primary),
        cardDef(8, '8', 'Eight', Colors.primary),
        cardDef(9, '9', 'Nine', Colors.primary),
        cardDef(10, '10', 'Ten', Colors.primary),
        cardDef(10, '11', 'Eleven', Colors.primary),
        cardDef(10, '12', 'Twelve', Colors.primary),
    ),
    cash: staticSetPackDef(
        cardDef(1, '€1', '€1', '#ff9800'),
        cardDef(2, '€2', '€2', '#ff9800'),
        cardDef(5, '€5', '€5', '#ff9800'),
        cardDef(10, '€10', '€10', '#ff9800'),
        cardDef(20, '€20', '€20', '#ff9800'),
        cardDef(50, '€50', '€50', '#ff9800'),
        cardDef(100, '€100', '€100', '#ff9800'),
        cardDef(500, '€500', '€500', '#ff9800'),
    ),
    gameWords: dynamicPoolPackDef(
        () => (PictWords as string[]).map(x => cardDef(0, x, x, Arr.random(ContrastColorList)))
    ),
};

export const cardPackCreate = defineCommand(
    'cardPackCreate',
    'Create: Card Pack',
    r => Physicals.createAtCameraPosition(r, CardPack, cp => {
        cp.param = 'planning';
    })
);

export const cardPileCreate = defineCommand(
    'cardPile',
    'Create: Card Pile',
    r => Physicals.createAtCameraPosition(r, CardPile, cp => {
        // cp.param = 'planning';
    })
);

export const cardShuffle = defineCommand(
    'cardShuffle',
    'Cards: Shuffle',
    (cc, [at, targetIds]: [Point?, string[]?]) => {

        const effects = cc.call(EffectDirector);
        const objects = cc.call(ObjectManager);

        const subjects = Phys.from(resolveSubjects(cc, targetIds))
            .polarize(ValueCard)
            .toArray();
        if (!subjects.length) return;

        const center = at ?? Physicals.bounds(subjects).center();
        const shuffled = shuffle(subjects);

        objects.update(() => {

            shuffled.forEach((vc, i) => {
                // Reorder all but first
                if (i > 0) {
                    const before = shuffled[i - 1];
                    before.parentPhysical.addBefore(before, vc.removeSelf());
                }

                const mb = center.subtract(vc.center(RelativeTo.Model));
                vc.moveBy(mb);
                // vc.concealed = true;
                effects.trigger(Effects.shuffle(), vc);
            });

        });

        cc.dispatch(clearSelection);
    }
);

export const dealerRevealAll = defineCommand(
    'dealerRevealAll',
    'Dealer: Reveal All',
    (cc) => {

        const objects = cc.call(ObjectManager);

        const cards = Array.from(objects.getAll(ValueCard))
            .filter(x => x.mounted());

        objects.update(() => {
            for (const c of cards) {
                c.concealed = false;
            }
        });
    }
);

export const cardShuffleAtPointer = defineCommand(
    'cardShuffle',
    'Cards: Shuffle',
    cc => {

        const camera = cc.call(Camera);
        const spatial = cc.call(SpatialIndex);
        const session = cc.call(EditorSession);

        const mpt = camera.toModel(session.get(PointerInfo).position);
        const selection = session.get(Selection);

        if (selection.size !== 0 && selection.uniform(ValueCard)) {
            cc.dispatch(cardShuffle, [mpt, selection.toArrayOfIds()]);
        }
        else {
            // Find all cards that are under the pointer, then expand to include any overlapping cards
            const hits = Array.from(new Set(
                Array.from(spatial.test(mpt, ValueCard))
                    .flatMap(x => Array.from(spatial.lookup(x.bounds(RelativeTo.Model), ValueCard)))
            ));

            if (hits.length < 2) return;
            cc.dispatch(cardShuffle, [mpt, hits.map(x => x.id)]);
        }
    }
);

export const createValueCard = defineCommand(
    'createValueCard',
    'Create: Value Card',
    (cc, [size]: [string?]) => Physicals.createAtCameraPosition(cc, ValueCard, vc => {
        vc.width = 140;
        if (size === 'sm' || size === 'small') {
            vc.style = 'small';
            vc.height = 140;
        }
        else {
            vc.height = 180;
        }
    }),
    [
        stringStep('size', 'Large/Small?', 'large')
    ]
);

export const handDeal = defineCommand(
    'handDeal',
    'Hand: Deal...',
    (r, [packId]: [string]) => {
        packId = packId ?? 'planning';

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const info = r.call(EditorSession).get(HandInfo);

        const packInst = objects.find(CardPack, x => x.id === packId || x.name === packId);
        const hand = objects.getMaybe(info.object, Hand);
        const packDef = packs[packInst?.param] ?? packs[packId] ?? packs.planning;

        if (!info.enabled || !hand || !packDef) return;

        dispatch(handPurge);

        objects.update(u => {

            const all = packDef.open();
            const picked = [] as CardDef[];

            if (packDef.mode === 'pool') {
                const rand = alea(packId, { global: true });
                const shuffled = shuffle(all, rand);
                const pick = !!packInst ? Arr.at(shuffled, packInst.counter) : Arr.random(shuffled);
                picked.push(pick);
            }
            else {
                picked.push(...all);
            }

            for (let i = 0; i < picked.length; i++) {
                const def = picked[i];
                const inst = u.create(ValueCard);
                inst.value = def.value;
                inst.smallText = def.smallText;
                inst.largeText = def.largeText;
                inst.color = def.color;
                inst.sort = i;
                inst.concealed = false;
                inst.moveTo(0, 0);
                hand.add(inst);
            }

            // Increment draw counter if pack instance provided
            if (!!packInst) {
                packInst.counter++;
            }

        });
    },
    [
        stringStep('pack', 'Which pack?', 'planning')
    ]
);

export const handReturnAll = defineCommand(
    'handReturnAll',
    'Hand: Return All',
    (r) => {

        const objects = r.call(ObjectManager);

        const allHands = Array.from(objects.getAll(Hand));
        const handsByUser = Arr.keyBy(allHands, 'userId' as any);
        const allCards = Array.from(objects.getAll(ValueCard));
        const cardsByUser = Arr.groupBy(allCards, 'userId' as any);

        // Do we have cards that need returning?
        const returnable = allCards.filter(x => x.parent?.type !== 'Hand');
        if (!returnable.length) return;

        // Get the users we need to resupply
        const users = new Set(returnable.map(x => x.userId).filter(x => !!x));

        objects.update(() => {

            for (const u of users) {
                const uh = handsByUser[u];
                const ucs = cardsByUser[u];
                if (!uh || !ucs?.length) continue;

                // Clear hand
                uh.removeAll();

                // Unmount cards
                ucs.forEach(x => {
                    if (x.parent) {
                        x.removeSelf();
                    }
                });

                // Add all back in order
                const ordered = ucs.slice().sort((a, b) => (a.sort ?? a.value ?? 0) - (b.sort ?? b.value ?? 0));
                ordered.forEach(x => {
                    uh.add(x);
                    x.moveTo(0, 0);
                    x.concealed = false;
                });
            }

        });
    }
);

export const handPurge = defineCommand(
    'handPurge',
    'Hand: Purge',
    (r) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);
        const info = session.get(HandInfo);
        const hand = objects.get(info.object, Hand);

        if (!info.enabled || !hand) return;

        session.apply(Selection, s => s.replace(hand.polarize(Physical)));
        dispatch(deleteSelection);
    }
);

export const handPlay = defineCommand(
    'handPlay',
    'Hand: Play',
    (cc, [targetId]: [string]) => {

        const camera = cc.call(Camera);
        const objects = cc.call(ObjectManager);
        const session = cc.call(EditorSession);
        const me = session.get(MyProfileInfo);
        const info = session.get(HandInfo);
        const hand = objects.get(info.object, Hand);
        const hold = cc.call(HoldLayout);

        if (!info.enabled || !hand) return;

        const target = objects.get(targetId, Physical);
        if (!target) return;

        const { transform } = hold.find(x => x.id === targetId) ?? {};
        const from = camera.toModel(transform.apply(Point.zero));

        const myZone = objects.find(DropZone, x => x.mode === DropZoneMode.participant && x.userId === me.id);
        const availableZones = objects.findAll(DropZone, x => x.mode === DropZoneMode.participant && !x.userId);
        const candidateZone = myZone ?? Phys.from(availableZones).nearestTo(from);
        if (!candidateZone) return;

        objects.update(() => {
            target.as(ValueCard, x => void (x.concealed = true));
            hand.remove(target);
            objects.getRoot().add(target);
            target.moveTo(from);
            cc.dispatch(dropZoneDrop, [candidateZone.id, [target.id]]);
        });
    }
);

export const inHandCommands = () => inCommands(
    createValueCard,
    cardPackCreate,
    cardPileCreate,
    cardShuffle,
    handDeal,
    handPurge,
    handReturnAll,
    dealerRevealAll,
);

